import { FC } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import { TradingOffer } from '../../../types/TradingOffer';

type TradingOfferDetailSummaryCardProps = {
  offer: TradingOffer;
  availableShares: number;
  daysRemaining: number;
};

const TradingOfferDetailSummaryCard: FC<TradingOfferDetailSummaryCardProps> = ({
  offer,
  availableShares,
  daysRemaining,
}) => {
  const { t } = useTranslate();

  const isSellOffer = offer.type === 'SELL';
  const totalAmount = offer.sharesCount.total * offer.sharePrice;

  const summaryItems = [
    {
      label: t('OfferType'),
      value: isSellOffer ? t('SellOffer') : t('BuyOffer'),
      icon: isSellOffer ? 'ni-wallet-out' : 'ni-wallet-in',
      color: isSellOffer ? 'danger' : 'success',
    },
    {
      label: t('SharesOffered'),
      value: formatNumber(offer.sharesCount.total),
      subValue: `${formatNumber(availableShares)} ${t(
        'Available'
      ).toLowerCase()}`,
      icon: 'ni-layers',
      color: 'primary',
    },
    {
      label: t('PricePerShare'),
      value: formatCurrency(offer.sharePrice),
      subValue: `${t('Total')}: ${formatCurrency(totalAmount)}`,
      icon: 'ni-sign-euro',
      color: 'warning',
    },
    {
      label: t('StartDate'),
      value: format(new Date(offer.startDate), dateFormat),
      icon: 'ni-calendar',
      color: 'info',
    },
    {
      label: t('EndDate'),
      value: format(new Date(offer.endDate), dateFormat),
      subValue:
        daysRemaining > 0
          ? `${daysRemaining} ${t('DaysRemaining').toLowerCase()}`
          : t('Expired'),
      icon: 'ni-calendar-check',
      color: 'secondary',
    },
    {
      label: t('PartialSale'),
      value: offer.isPartialAmountEnabled ? t('Yes') : t('No'),
      icon: 'ni-swap',
      color: 'gray',
    },
  ];

  return (
    <div className="card card-bordered">
      <div className="card-inner">
        <h6 className="card-title text-base">{t('OfferSummary')}</h6>

        <div className="row">
          {[0, 1].map((colIndex) => (
            <div key={colIndex} className="col-md-6">
              <ul className="nk-olist nk-olist-flat">
                {summaryItems
                  .slice(colIndex * 3, colIndex * 3 + 3)
                  .map((item) => (
                    <li
                      key={item.label}
                      className="nk-olist-item d-flex align-items-start py-2"
                    >
                      <div
                        className={`nk-olist-icon text-${item.color} mr-3`}
                        style={{
                          borderLeft: `3px solid`,
                          paddingLeft: '12px',
                        }}
                      >
                        <em className={`icon ni ${item.icon} fs-24px`} />
                      </div>
                      <div className="nk-olist-body flex-grow-1">
                        <span className="sub-text d-block">{item.label}</span>
                        <span className="lead-text">{item.value}</span>
                        {item.subValue && (
                          <span className="sub-text d-block text-soft">
                            {item.subValue}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TradingOfferDetailSummaryCard;
